import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
} from "@mui/material";
import {
  ArrowBackOutlined,
  DeleteOutlineOutlined,
  DragIndicatorOutlined,
  AccessTimeOutlined,
  ImageOutlined,
  VideocamOutlined,
  LanguageOutlined,
  PictureAsPdfOutlined,
  WidgetsOutlined,
  DesignServicesOutlined,
  PlayArrowOutlined,
  AddOutlined,
  QueueMusicOutlined,
  AssignmentOutlined,
} from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { api } from "../../api/api";
import { getContent } from "../../Redux/Actions/contentAction";
import Layout from "../../components/layout/Layout";
import Loader from "../../components/loader/Loader";
import toast from "react-hot-toast";
import "./playlist-details.css";
import { canEditCms } from "../../utils/accessControl";
import { useContextUserId } from "../../hooks/useSupportContext";
import AssignScreensDialog from "./components/AssignScreensDialog";
import { formatMs, sumDurationsMs } from "./formatMs";

function PlaylistDetails({ user, socketRef }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const canEdit = canEditCms(user);
  const contextUserId = useContextUserId();
  const { content } = useSelector((state) => state.Content);

  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [dragSource, setDragSource] = useState(null);
  const [durationDialog, setDurationDialog] = useState({ open: false, item: null });
  const [durationFields, setDurationFields] = useState({ minutes: 0, seconds: 0 });
  const [openAssignDialog, setOpenAssignDialog] = useState(false);

  const fetchPlaylist = useCallback(async () => {
    try {
      const { data } = await api.get(`/playlist/getplaylistbyid/${id}`);
      setPlaylist(data.playlist);
    } catch (err) {
      toast.error("Failed to load playlist");
      navigate("/playlist");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchPlaylist();
    if (contextUserId) {
      dispatch(getContent(contextUserId, { page: 1, limit: 100 }));
    }
  }, [fetchPlaylist, contextUserId, dispatch]);

  /** Client-side delegate (backup); server also emits after playlist API saves */
  const emitDelegateMacs = useCallback((macList) => {
    if (!macList?.length || !socketRef.current) return;
    macList.forEach((mac) => {
      socketRef.current.emit("event:delegate", { detail: { mac } });
    });
  }, [socketRef]);

  const handleAddToPlaylist = async (contentItem) => {
    try {
      const res = await api.patch(`/playlist/addcontent/${id}`, {
        content: [contentItem],
      });
      if (res.status === 200 || res.status === 201) {
        const pl = res.data.playlist;
        setPlaylist(pl);
        toast.success("Added to playlist");
        emitDelegateMacs(pl?.mac_list);
      }
    } catch {
      toast.error("Failed to add content");
    }
  };

  const handleRemoveFromPlaylist = async (contentId) => {
    try {
      const res = await api.patch(`/playlist/deletecontentfromplaylist/${id}`, {
        contentId,
      });
      const updated = res.data.updatedPlaylist;
      if (updated) {
        setPlaylist(updated);
        emitDelegateMacs(updated.mac_list);
      } else {
        setPlaylist((prev) => ({
          ...prev,
          content: prev.content.filter((c) => c._id !== contentId),
        }));
        emitDelegateMacs(playlist?.mac_list);
      }
      toast.success("Removed from playlist");
    } catch {
      toast.error("Failed to remove content");
    }
  };

  const handleDurationUpdate = async (contentId, newSecondsTotal) => {
    const min = Math.floor(newSecondsTotal / 60);
    const sec = newSecondsTotal % 60;
    const res = await api.patch(`/playlist/updateduration/${id}`, {
      time: `${min}:${sec}`,
      contentId,
    });
    const pl = res.data.playlist;
    if (pl) {
      setPlaylist(pl);
      emitDelegateMacs(pl.mac_list);
    } else {
      setPlaylist((prev) => ({
        ...prev,
        content: prev.content.map((c) =>
          c._id === contentId ? { ...c, duration: newSecondsTotal * 1000 } : c
        ),
      }));
      emitDelegateMacs(playlist?.mac_list);
    }
    toast.success("Duration updated");
  };

  const openDurationDialog = (item) => {
    const totalSeconds = Math.floor((item.duration || 0) / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    setDurationFields({ minutes, seconds });
    setDurationDialog({ open: true, item });
  };

  const saveDurationDialog = async () => {
    if (!durationDialog.item) return;
    const totalSeconds = durationFields.minutes * 60 + durationFields.seconds;
    if (totalSeconds < 0 || !Number.isFinite(totalSeconds)) {
      toast.error("Invalid duration");
      return;
    }
    try {
      await handleDurationUpdate(durationDialog.item._id, totalSeconds);
      setDurationDialog({ open: false, item: null });
    } catch {
      toast.error("Failed to update duration");
    }
  };

  // Drag from media library to playlist
  const handleMediaDragStart = (e, item) => {
    setDragSource("media");
    setDraggedItem(item);
    e.dataTransfer.effectAllowed = "copy";
  };

  // Drag within playlist to reorder
  const handlePlaylistDragStart = (e, item, index) => {
    setDragSource("playlist");
    setDraggedItem({ item, index });
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDropOnPlaylist = (e, dropIndex) => {
    e.preventDefault();
    setDragOverIndex(null);

    if (dragSource === "media" && draggedItem) {
      handleAddToPlaylist(draggedItem);
    } else if (dragSource === "playlist" && draggedItem) {
      if (draggedItem.index === dropIndex) {
        setDraggedItem(null);
        return;
      }
      const items = Array.from(playlist.content);
      const [moved] = items.splice(draggedItem.index, 1);
      items.splice(dropIndex, 0, moved);
      setPlaylist((prev) => ({ ...prev, content: items }));
      api.post("/playlist/playlistcontnetsorting", {
        playlistId: id,
        content: items,
      });
      emitDelegateMacs(playlist?.mac_list);
    }

    setDraggedItem(null);
    setDragSource(null);
  };

  const handleDropOnPlaylistZone = (e) => {
    e.preventDefault();
    setDragOverIndex(null);
    if (dragSource === "media" && draggedItem) {
      handleAddToPlaylist(draggedItem);
    }
    setDraggedItem(null);
    setDragSource(null);
  };

  const typeIcon = (type) => {
    const sx = { fontSize: 16 };
    switch (type) {
      case "image": return <ImageOutlined sx={{ ...sx, color: "#3B82F6" }} />;
      case "video": return <VideocamOutlined sx={{ ...sx, color: "#8B5CF6" }} />;
      case "url": return <LanguageOutlined sx={{ ...sx, color: "#F59E0B" }} />;
      case "application": return <PictureAsPdfOutlined sx={{ ...sx, color: "#EF4444" }} />;
      case "widget": return <WidgetsOutlined sx={{ ...sx, color: "#10B981" }} />;
      case "template": return <DesignServicesOutlined sx={{ ...sx, color: "#EC4899" }} />;
      default: return <ImageOutlined sx={sx} />;
    }
  };

  const typeLabel = (type) => {
    if (type === "application") return "PDF";
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : "—";
  };

  const formatDuration = (ms) => {
    return formatMs(ms);
  };

  const getTotalDuration = () => {
    if (!playlist?.content) return "0:00";
    const total = sumDurationsMs(playlist.content);
    return formatDuration(total);
  };


  if (loading) {
    return (
      <Layout title="Playlist" user={user}>
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <Loader />
        </Box>
      </Layout>
    );
  }

  if (!playlist) return null;

  return (
    <Layout title="Playlist" user={user}>
      <div className="parent-container playlist-details-page">
        {/* Header bar */}
        <Paper
          elevation={0}
          className="pd-details-toolbar"
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 2,
            padding: "12px 20px",
            borderRadius: "14px",
            border: "1px solid #E5E7EB",
            backgroundColor: "#fff",
            mb: "12px",
          }}
        >
          <IconButton onClick={() => navigate("/playlist")} size="small">
            <ArrowBackOutlined />
          </IconButton>
          <QueueMusicOutlined sx={{ color: "#008080", fontSize: 24 }} />
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#111827",
              }}
            >
              {playlist.name}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Inter', sans-serif",
                fontSize: "12px",
                color: "#6B7280",
              }}
            >
              {playlist.content?.length || 0} items &bull; {getTotalDuration()} total
              {playlist.mac_list?.length > 0 && (
                <>
                  {" "}&bull; {playlist.mac_list.length} screen
                  {playlist.mac_list.length !== 1 ? "s" : ""}
                  {playlist.mac_list.length > 1 && " (shared)"}
                </>
              )}
            </Typography>
          </Box>
          {canEdit && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AssignmentOutlined />}
              onClick={() => setOpenAssignDialog(true)}
              sx={{
                textTransform: "none",
                borderColor: "#008080",
                color: "#008080",
                fontWeight: 600,
                "&:hover": { borderColor: "#006666", backgroundColor: "#F0FDFA" },
              }}
            >
              {playlist.mac_list?.length > 1 ? "Manage screens" : "Assign to screens"}
            </Button>
          )}
        </Paper>

        <AssignScreensDialog
          open={openAssignDialog}
          onClose={() => setOpenAssignDialog(false)}
          playlist={playlist}
          userId={contextUserId}
          onAssigned={() => fetchPlaylist()}
        />

        {/* Split view */}
        <div className="pd-split">
          {/* LEFT — Playlist items */}
          <Paper
            elevation={0}
            className="pd-panel"
            sx={{
              flex: 1,
              borderRadius: "16px",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 220px)",
            }}
            onDragOver={canEdit ? (e) => e.preventDefault() : undefined}
            onDrop={canEdit ? handleDropOnPlaylistZone : undefined}
          >
            <Box className="pd-panel-header">
              <Typography className="pd-panel-title">
                Playlist Items
              </Typography>
              <Chip
                label={`${playlist.content?.length || 0}`}
                size="small"
                sx={{
                  backgroundColor: "#F0FDFA",
                  color: "#008080",
                  fontWeight: 600,
                  fontSize: "12px",
                  fontFamily: "'Inter', sans-serif",
                }}
              />
            </Box>

            <Box className="pd-items-list">
              {!playlist.content || playlist.content.length === 0 ? (
                <Box className="pd-empty">
                  <Typography sx={{ color: "#9CA3AF", fontSize: "14px", fontFamily: "'Inter', sans-serif" }}>
                    {canEdit
                      ? "Drag content from the right panel to add items"
                      : "No items in this playlist"}
                  </Typography>
                </Box>
              ) : (
                playlist.content.map((item, index) => (
                  <div
                    key={item._id}
                    className={`pd-item ${dragOverIndex === index ? "pd-item--dragover" : ""}`}
                    draggable={canEdit}
                    onDragStart={canEdit ? (e) => handlePlaylistDragStart(e, item, index) : undefined}
                    onDragOver={canEdit ? (e) => handleDragOver(e, index) : undefined}
                    onDragLeave={canEdit ? handleDragLeave : undefined}
                    onDrop={canEdit ? (e) => handleDropOnPlaylist(e, index) : undefined}
                  >
                    {canEdit && <DragIndicatorOutlined className="pd-item-drag" />}
                    <span className="pd-item-index">{index + 1}</span>

                    {item.url && item.type === "image" ? (
                      <img src={item.url} alt="" className="pd-item-thumb" loading="lazy" decoding="async" />
                    ) : (
                      <Box className="pd-item-icon-box">
                        {typeIcon(item.type)}
                      </Box>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography className="pd-item-name">
                        {(item.title || item.name || "Untitled").substring(0, 35)}
                      </Typography>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.3 }}>
                        <span className="pd-item-type">{typeLabel(item.type)}</span>
                        <span className="pd-item-duration">
                          <AccessTimeOutlined sx={{ fontSize: 12 }} />
                          {formatDuration(item.duration)}
                        </span>
                      </Box>
                    </Box>

                    {canEdit && (
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Tooltip title="Set Duration">
                        <IconButton
                          size="small"
                          onClick={() => openDurationDialog(item)}
                        >
                          <AccessTimeOutlined sx={{ fontSize: 18, color: "#008080" }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove">
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveFromPlaylist(item._id)}
                        >
                          <DeleteOutlineOutlined sx={{ fontSize: 18, color: "#EF4444" }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    )}
                  </div>
                ))
              )}
            </Box>
          </Paper>

          {/* RIGHT — Media Library */}
          <Paper
            elevation={0}
            className="pd-panel"
            sx={{
              flex: 1,
              borderRadius: "16px",
              border: "1px solid #E5E7EB",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
              maxHeight: "calc(100vh - 220px)",
            }}
          >
            <Box className="pd-panel-header">
              <Typography className="pd-panel-title">
                Media Library
              </Typography>
              <Chip
                label={`${content.length}`}
                size="small"
                sx={{
                  backgroundColor: "#EFF6FF",
                  color: "#3B82F6",
                  fontWeight: 600,
                  fontSize: "12px",
                  fontFamily: "'Inter', sans-serif",
                }}
              />
            </Box>

            <Box className="pd-media-grid-wrap">
              {content.length === 0 ? (
                <Box className="pd-empty">
                  <Typography sx={{ color: "#9CA3AF", fontSize: "14px", fontFamily: "'Inter', sans-serif" }}>
                    No media found
                  </Typography>
                </Box>
              ) : (
                <div className="pd-media-grid">
                  {content.map((item, index) => (
                    <div
                      key={item.id || index}
                      className="pd-media-card"
                      draggable={canEdit}
                      onDragStart={canEdit ? (e) => handleMediaDragStart(e, item) : undefined}
                    >
                      <div className="pd-media-card-thumb">
                        {item.url && item.type === "image" ? (
                          <img src={item.url} alt="" loading="lazy" decoding="async" />
                        ) : item.url && item.type === "video" ? (
                          <Box className="pd-media-card-icon">
                            <VideocamOutlined sx={{ fontSize: 22, color: "#8B5CF6" }} />
                          </Box>
                        ) : (
                          <Box className="pd-media-card-icon">
                            {typeIcon(item.type)}
                          </Box>
                        )}
                        {canEdit && (
                        <Tooltip title="Add to Playlist">
                          <IconButton
                            size="small"
                            className="pd-media-card-add"
                            onClick={() => handleAddToPlaylist(item)}
                          >
                            <AddOutlined sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                        )}
                      </div>
                      <div className="pd-media-card-info">
                        <span className="pd-media-card-name">
                          {(item.name || "Untitled").substring(0, 20)}
                        </span>
                        <span className="pd-media-card-type">{typeLabel(item.type)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Box>
          </Paper>
        </div>
      </div>

      <Dialog
        open={durationDialog.open}
        onClose={() => setDurationDialog({ open: false, item: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit duration</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Content:{" "}
            {(durationDialog.item?.title || durationDialog.item?.name || "Untitled").substring(0, 80)}
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                label="Minutes"
                type="number"
                fullWidth
                value={durationFields.minutes}
                onChange={(e) =>
                  setDurationFields((d) => ({
                    ...d,
                    minutes: Math.max(0, parseInt(e.target.value, 10) || 0),
                  }))
                }
                inputProps={{ min: 0, max: 999 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Seconds"
                type="number"
                fullWidth
                value={durationFields.seconds}
                onChange={(e) =>
                  setDurationFields((d) => ({
                    ...d,
                    seconds: Math.min(59, Math.max(0, parseInt(e.target.value, 10) || 0)),
                  }))
                }
                inputProps={{ min: 0, max: 59 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDurationDialog({ open: false, item: null })}>Cancel</Button>
          <Button variant="contained" onClick={saveDurationDialog}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}

export default PlaylistDetails;
